import { useState } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const honeypotValue = (form.elements.namedItem("website_alt") as HTMLInputElement)?.value?.trim();

    if (honeypotValue) {
      setFormStatus("success");
      setEmail("");
      return;
    }

    const formData = new FormData(form);

    try {
      const response = await fetch("https://readdy.ai/api/form/d99kvi7k7gok24d4ae30", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        setEmail("");
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormStatus("error");
        setFormError(serverMsg || "Something went wrong. Please try again.");
      }
    } catch {
      setFormStatus("error");
      setFormError("Network error. Please check your connection and try again.");
    }
  };

  const navColumns = [
    {
      title: "Resources",
      links: [
        { label: "Complete Guide", to: "/hipobuy-spreadsheet-guide" },
        { label: "Tutorial", to: "/hipobuy-spreadsheet-tutorial" },
        { label: "Safety", to: "/hipobuy-spreadsheet-safety" },
        { label: "Review", to: "/hipobuy-spreadsheet-review" },
        { label: "Categories Guide", to: "/hipobuy-spreadsheet-categories" },
        { label: "Best Resources", to: "/best-spreadsheet-resources" },
        { label: "Alternatives", to: "/hipobuy-spreadsheet-alternatives" },
      ],
    },
    {
      title: "Comparisons",
      links: [
        { label: "Kakobuy", to: "/kakobuy-spreadsheet" },
        { label: "CSSBuy", to: "/cssbuy-spreadsheet" },
        { label: "Superbuy", to: "/superbuy-spreadsheet" },
        { label: "Oopbuy", to: "/oopbuy-spreadsheet" },
        { label: "Beginner Mistakes", to: "/hipobuy-spreadsheet-beginner-mistakes" },
      ],
    },
    {
      title: "Categories",
      links: categories.map((cat) => ({
        label: cat.name,
        href: cat.url,
        external: true,
      })),
    },
  ];

  return (
    <footer className="bg-background-200/60 border-t border-background-200/70">
      <div className="px-4 md:px-6 max-w-[1200px] mx-auto py-14 md:py-20">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
          {/* Left: Brand + Newsletter */}
          <div className="lg:w-2/5">
            <h3 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 leading-tight">
              Hipobuy<br />Spreadsheet
            </h3>
            <p className="text-sm text-foreground-600 mt-4 max-w-sm leading-relaxed">
              Your ultimate resource hub for <strong>Hipobuy Spreadsheet</strong> guides, product research tools, 
              platform comparisons, and expert shopping tips. Updated regularly with the latest 2026 data.
            </p>

            {/* Newsletter */}
            <div className="mt-8">
              <p className="text-sm font-semibold text-foreground-800 mb-3">Subscribe for Updates</p>
              <form
                onSubmit={handleNewsletterSubmit}
                data-readdy-form
                className="flex flex-col sm:flex-row gap-3"
              >
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <input
                  type="email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  className="flex-1 px-4 py-3 bg-transparent border-b-2 border-secondary-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                />
                <button
                  type="submit"
                  className="px-6 py-3 border-2 border-primary-500 text-primary-500 font-semibold text-sm rounded-lg hover:bg-primary-500 hover:text-background-50 transition-all whitespace-nowrap cursor-pointer"
                >
                  Join Newsletter
                </button>
              </form>
              {formStatus === "success" && (
                <p className="text-sm text-accent-600 mt-2 font-medium">Thanks for subscribing! Check your inbox soon.</p>
              )}
              {formStatus === "error" && (
                <p className="text-sm text-primary-500 mt-2 font-medium">{formError}</p>
              )}
            </div>
          </div>

          {/* Right: Navigation Columns */}
          <div className="lg:w-3/5 grid grid-cols-2 md:grid-cols-3 gap-8">
            {navColumns.map((col) => (
              <div key={col.title}>
                <h4 className="text-sm font-semibold text-foreground-900 mb-4 uppercase tracking-wider">
                  {col.title}
                </h4>
                <ul className="flex flex-col gap-2.5">
                  {col.links.map((link, linkIdx) => (
                    <li key={linkIdx}>
                      {"external" in link && link.external ? (
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener nofollow"
                          className="text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
                        >
                          {link.label}
                        </a>
                      ) : (
                        <Link
                          to={"to" in link ? link.to : "#"}
                          className="text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
                        >
                          {link.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-background-200/50 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-500">
            &copy; {new Date().getFullYear()} Hipobuy Spreadsheet. This website provides educational information only and does not guarantee product quality, seller reliability, or transaction outcomes.
          </p>
          <div className="flex items-center gap-6">
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-xs text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer">
              Main Site
            </a>
            <Link to="/hipobuy-spreadsheet-safety" className="text-xs text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer">
              Safety
            </Link>
            <Link to="/hipobuy-spreadsheet-guide" className="text-xs text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer">
              Guide
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}